<div class="enroll-page">

  <!-- ── School branding ─────────────────────────────────────────────────── -->
  <header class="school-header">
    <div class="school-logo-placeholder">
      @if (school?.logo) {
        <!-- The logo is stored as a media path, like on the school profile. -->
        <img [src]="school!.logo | filePath" [alt]="school!.name" class="school-logo-img" />
      } @else {
        <mat-icon class="school-logo-icon">school</mat-icon>
      }
    </div>
    <p class="school-name">{{ school?.name || 'School' }}</p>
  </header>

  <!-- ── Loading / error / empty ─────────────────────────────────────────── -->
  @if (loading) {
    <div class="enroll-container state-block">
      <mat-spinner diameter="36"></mat-spinner>
    </div>
  } @else if (loadError) {
    <div class="enroll-container state-block">
      <mat-icon class="state-icon">error_outline</mat-icon>
      <p class="state-text">We couldn't load this booking page. Please try again later.</p>
      <button mat-stroked-button (click)="loadProducts()">Retry</button>
    </div>
  } @else if (activities.length === 0) {
    <div class="enroll-container state-block">
      <mat-icon class="state-icon">event_busy</mat-icon>
      <p class="state-text">This school has no activities available for online booking yet.</p>
    </div>
  } @else {

    <!-- ── Step 1 · Activity selection ──────────────────────────────────── -->
    @if (step === 1) {
      <div class="enroll-container">
        <div class="step-intro">
          <h1 class="step-title">Choose your activity</h1>
          <p class="step-subtitle">Select what you'd like to do and we'll guide you through the rest.</p>
        </div>

        <div class="activity-grid">
          @for (activity of activities; track activity.activityId) {
            <div
              class="activity-card activity-color-{{ activity.colorIndex }}"
              (click)="selectActivity(activity.activityId)"
              role="button"
              [attr.aria-label]="'Select ' + activity.name"
              tabindex="0"
              (keydown.enter)="selectActivity(activity.activityId)"
              (keydown.space)="$event.preventDefault(); selectActivity(activity.activityId)"
            >
              <div class="activity-image">
                @if (activity.imageUrl) {
                  <img [src]="activity.imageUrl" [alt]="activity.name" class="activity-img" />
                } @else {
                  <div class="activity-img-placeholder"></div>
                }
              </div>
              <div class="activity-info">
                <h3 class="activity-name">{{ activity.name }}</h3>
                <p class="activity-price">From {{ activity.fromPrice }} €</p>
              </div>
              <div class="activity-arrow">
                <mat-icon>arrow_forward</mat-icon>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- ── Step 2 · Product selection ───────────────────────────────────── -->
    @if (step === 2) {
      <div class="enroll-container product-topbar">
        <button class="back-btn" (click)="back()">
          <mat-icon>arrow_back</mat-icon> Back
        </button>
      </div>

      <div class="enroll-container product-page">
        <div class="product-content">
          <div class="product-cover activity-color-{{ currentActivity?.colorIndex ?? 0 }}">
            @if (coverImageUrl) {
              <img [src]="coverImageUrl" [alt]="currentActivity?.name" class="product-cover-img" />
            } @else {
              <div class="product-cover-placeholder"></div>
            }
          </div>
          <h1 class="product-title">{{ currentActivity?.name }}</h1>
          <p class="product-description">
            {{ selectedProduct?.publicDescription || 'Choose a format and level, then pick the package that suits you best.' }}
          </p>
        </div>

        <aside class="selection-panel">
          <div class="panel-card">
            <h2 class="panel-title">Book your spot</h2>

            <!-- Modality filter -->
            <div class="panel-section">
              <p class="panel-label">Format</p>
              <div class="pill-group">
                @for (mod of modalities; track mod.modalityId) {
                  <button
                    class="pill"
                    [class.pill--active]="selectedModalityId === mod.modalityId"
                    (click)="onSelectModality(mod.modalityId)"
                  >{{ mod.name }}</button>
                }
              </div>
            </div>

            <!-- Level filter — once a modality is chosen -->
            @if (selectedModalityId && levels.length > 0) {
              <div class="panel-section">
                <p class="panel-label">Level</p>
                <div class="pill-group">
                  @for (lvl of levels; track lvl.levelId) {
                    <button
                      class="pill"
                      [class.pill--active]="selectedLevelId === lvl.levelId"
                      (click)="onSelectLevel(lvl.levelId)"
                    >{{ lvl.name }}</button>
                  }
                </div>
              </div>
            }

            <!-- Packages — product × price tier, once filters are resolved -->
            @if (levelSelectionSatisfied && packageOptions.length > 0) {
              <div class="panel-section">
                <p class="panel-label">Package</p>
                <div class="product-options" role="radiogroup" aria-label="Select a package">
                  @for (opt of packageOptions; track opt.priceRowId) {
                    <div
                      class="product-option"
                      [class.product-option--selected]="selectedPriceRowId === opt.priceRowId"
                      (click)="selectPackage(opt)"
                      role="radio"
                      [attr.aria-checked]="selectedPriceRowId === opt.priceRowId"
                      [attr.aria-label]="opt.title + ' ' + opt.durationLabel"
                      tabindex="0"
                      (keydown.enter)="selectPackage(opt)"
                      (keydown.space)="$event.preventDefault(); selectPackage(opt)"
                    >
                      <div class="option-left">
                        <p class="option-title">{{ opt.title }}</p>
                        <p class="option-hours">
                          <mat-icon class="option-icon">schedule</mat-icon> {{ opt.durationLabel }}
                          @if (opt.levelName) {
                            <span class="option-level">· {{ opt.levelName }}</span>
                          }
                        </p>
                      </div>
                      <div class="option-right">
                        <p class="option-price">{{ opt.price }} €</p>
                        <mat-icon
                          class="option-check"
                          [class.option-check--on]="selectedPriceRowId === opt.priceRowId"
                        >{{ selectedPriceRowId === opt.priceRowId ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }

            <div class="panel-footer">
              @if (selectedPackage) {
                <div class="panel-price-row">
                  <span class="price-label">Total</span>
                  <span class="price-amount">{{ selectedPackage.price }} €</span>
                </div>
              }
              <button
                mat-flat-button color="primary" class="continue-btn"
                [disabled]="!selectedPriceRowId" (click)="goToDateStep()"
              >Continue <mat-icon>arrow_forward</mat-icon></button>
            </div>
          </div>
        </aside>
      </div>
    }

    <!-- ── Step 3 · Stay dates & preferred time ─────────────────────────── -->
    @if (step === 3) {
      <div class="enroll-container product-topbar">
        <button class="back-btn" (click)="back()">
          <mat-icon>arrow_back</mat-icon> Back
        </button>
      </div>

      <div class="enroll-container narrow-step">
        <h1 class="step-title">When are you here?</h1>
        <p class="step-subtitle m-b-24">
          Tell us your dates and, if you like, a preferred time. The school confirms
          your request and arranges the exact schedule with you.
        </p>

        <div class="form-card">
          <!-- What you're booking -->
          <div class="booking-strip">
            <div class="strip-info">
              <p class="strip-title">{{ selectedPackage?.title }}</p>
              <p class="strip-sub">{{ currentActivity?.name }} · {{ selectedPackage?.durationLabel }}</p>
            </div>
            <p class="strip-price">{{ selectedPackage?.price }} €</p>
          </div>

          <!-- Stay dates -->
          <div class="field-row">
            <mat-form-field appearance="outline" class="w-100">
              <mat-label>Arrival</mat-label>
              <input
                matInput readonly
                [matDatepicker]="arrivalPicker" [min]="today"
                [(ngModel)]="arrivalDateObj" (ngModelChange)="onArrivalChange()"
                (click)="arrivalPicker.open()"
              />
              <mat-datepicker-toggle matSuffix [for]="arrivalPicker"></mat-datepicker-toggle>
              <mat-datepicker #arrivalPicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-100">
              <mat-label>Departure</mat-label>
              <input
                matInput readonly
                [matDatepicker]="departurePicker" [min]="arrivalDateObj || today"
                [(ngModel)]="departureDateObj"
                (click)="departurePicker.open()"
              />
              <mat-datepicker-toggle matSuffix [for]="departurePicker"></mat-datepicker-toggle>
              <mat-datepicker #departurePicker></mat-datepicker>
            </mat-form-field>
          </div>

          <!-- Preferred time (optional) -->
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Preferred start time (optional)</mat-label>
            <mat-select [(ngModel)]="preferredTime" panelClass="time-panel">
              <mat-option [value]="''">No preference — school decides</mat-option>
              @for (slot of timeSlots; track slot) {
                <mat-option [value]="slot">{{ slot }}</mat-option>
              }
            </mat-select>
            <mat-icon matSuffix>schedule</mat-icon>
          </mat-form-field>

          <div class="request-note">
            <mat-icon>info</mat-icon>
            <span>This is a request, not a confirmed slot. The school will get in touch to finalise your schedule.</span>
          </div>
        </div>

        @if (submitError) {
          <p class="submit-error" role="alert"><mat-icon>error_outline</mat-icon> {{ submitError }}</p>
        }
        <div class="step-cta">
          <button
            mat-flat-button color="primary" class="continue-btn"
            [disabled]="!stayValid" (click)="goToDetailsStep()"
          >Continue <mat-icon>arrow_forward</mat-icon></button>
        </div>
      </div>
    }

    <!-- ── Step 4 · Customer details ────────────────────────────────────── -->
    @if (step === 4) {
      <div class="enroll-container product-topbar">
        <button class="back-btn" (click)="back()">
          <mat-icon>arrow_back</mat-icon> Back
        </button>
      </div>

      <div class="enroll-container narrow-step">
        <h1 class="step-title">Your details</h1>
        <p class="step-subtitle m-b-24">We'll send your booking confirmation here.</p>

        <div class="form-card">
          <div class="field-row">
            <mat-form-field appearance="outline" class="w-100">
              <mat-label>First name</mat-label>
              <input matInput [(ngModel)]="firstName" required />
            </mat-form-field>
            <mat-form-field appearance="outline" class="w-100">
              <mat-label>Last name</mat-label>
              <input matInput [(ngModel)]="lastName" />
            </mat-form-field>
          </div>

          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Email</mat-label>
            <input matInput type="email" [(ngModel)]="email" required />
            @if (email.trim() && !emailValid) {
              <mat-hint class="field-error">Check the email address</mat-hint>
            }
          </mat-form-field>

          <!-- Country list with flags and search, as in Team; gives +34…
               The label always sits on the border: the flag fills the field
               from the start, so a resting label would overlap it. -->
          <mat-form-field appearance="outline" class="w-100 tel-field" floatLabel="always">
            <mat-label>Phone</mat-label>
            <mat-tel-input
              [(ngModel)]="phone"
              [preferredCountries]="['es', 'gb', 'de', 'fr', 'it', 'nl']"
              [enableSearch]="true"
              format="national"
              name="phone"
            ></mat-tel-input>
          </mat-form-field>
        </div>

        @if (submitError) {
          <p class="submit-error" role="alert"><mat-icon>error_outline</mat-icon> {{ submitError }}</p>
        }
        <div class="step-cta">
          <button
            mat-flat-button color="primary" class="continue-btn"
            [disabled]="!detailsValid" (click)="goToReviewStep()"
          >Review booking <mat-icon>arrow_forward</mat-icon></button>
        </div>
      </div>
    }

    <!-- ── Step 5 · Review & confirm ────────────────────────────────────── -->
    @if (step === 5) {
      <div class="enroll-container product-topbar">
        <button class="back-btn" (click)="back()">
          <mat-icon>arrow_back</mat-icon> Back
        </button>
      </div>

      <div class="enroll-container narrow-step">
        <h1 class="step-title">Review your request</h1>

        <div class="summary-card">
          <div class="summary-row">
            <span class="summary-label">Activity</span>
            <span class="summary-value">{{ currentActivity?.name }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Package</span>
            <span class="summary-value">{{ selectedPackage?.title }} · {{ selectedPackage?.durationLabel }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Dates</span>
            <span class="summary-value">{{ stayLabel }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Preferred time</span>
            <span class="summary-value">{{ preferredLabel }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Name</span>
            <span class="summary-value">{{ firstName }} {{ lastName }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Email</span>
            <span class="summary-value">{{ email }}</span>
          </div>
          @if (phoneDisplay) {
            <div class="summary-row">
              <span class="summary-label">Phone</span>
              <span class="summary-value">{{ phoneDisplay }}</span>
            </div>
          }
          <div class="summary-row summary-row--total">
            <span class="summary-label">Total</span>
            <span class="price-amount">{{ selectedPackage?.price }} €</span>
          </div>
        </div>

        <p class="pay-note">
          <mat-icon class="info-block-icon">info</mat-icon>
          No online payment required. The school will review your request and confirm your schedule.
        </p>

        @if (submitError) {
          <p class="submit-error" role="alert"><mat-icon>error_outline</mat-icon> {{ submitError }}</p>
        }
        <div class="step-cta">
          <button
            mat-flat-button color="primary" class="continue-btn"
            [disabled]="submitting" (click)="confirmBooking()"
          >
            @if (submitting) { Sending… } @else { Send request }
          </button>
        </div>
      </div>
    }

    <!-- ── Step 6 · Success ─────────────────────────────────────────────── -->
    @if (step === 6) {
      <div class="enroll-container narrow-step success-block">
        <mat-icon class="success-icon">check_circle</mat-icon>
        <h1 class="step-title">Request received!</h1>
        <p class="step-subtitle">
          We've sent a summary to <strong>{{ email }}</strong>.
          {{ school?.name }} will review it and be in touch to confirm your schedule.
        </p>
        <button mat-stroked-button (click)="resetFlow()" class="m-t-16">Make another booking</button>
      </div>
    }

  }
</div>
